import React, { useContext } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';

import { useTourContext } from './TourContext';
import { styles, getTourSteps, getTourConfig } from './util';
import { UserContext, ThemeContext } from 'src/store/ContextStore';
import CustomTooltip from './CustomTooltip';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export default function TourWrapper({ systemFeatures }) {
  const { theme } = useContext(ThemeContext);
  const {
    setState,
    state: { run, stepIndex, steps },
    startTour,
    endTour
  } = useTourContext();
  const { user } = useContext(UserContext);
  const { tourSkip = true, userType } = user || {};

  const t = useAutoTranslation();

  const navigate = useNavigate();

  useMount(() => {
    if (!tourSkip) {
      // Show virtual tour
      startTour(userType);
    }
  });

  // Steps depend on feature flags, which load asynchronously — rebuild
  // whenever they change so disabled features drop out of the tour.
  React.useEffect(() => {
    setState({
      steps: getTourSteps(userType, systemFeatures)
    });
  }, [userType, systemFeatures, setState]);

  const tourStepHandle = (data) => {
    const { action, index, nextLink, prevLink, end } = data;

    if (action === ACTIONS.NEXT) {
      if (end) return endTour();

      setState({ stepIndex: index + 1 });
      if (nextLink) navigate(nextLink);
    } else {
      setState({ stepIndex: index - 1 });
      if (prevLink) navigate(prevLink);
    }
  };

  const handleCallBack = (data) => {
    const userTypeConfig = getTourConfig(userType, systemFeatures);

    for (const step of userTypeConfig) {
      const { index: stepIndex, nextLink, prevLink, end } = step;
      const { action, index, type, status } = data;

      if (
        action === ACTIONS.CLOSE ||
        (status === STATUS.SKIPPED && run) ||
        status === STATUS.FINISHED
      ) {
        return endTour();
      } else if (type === EVENTS.STEP_AFTER && index === stepIndex) {
        return tourStepHandle({
          action,
          index,
          prevLink,
          nextLink,
          end
        });
      }
    }
  };

  return (
    <Joyride
      callback={handleCallBack}
      tooltipComponent={CustomTooltip}
      continuous
      run={run}
      stepIndex={stepIndex}
      steps={steps}
      disableScrollParentFix={true}
      disableScrolling={true}
      disableCloseOnEsc={true}
      disableOverlayClose={true}
      styles={{
        ...styles,
        options: {
          arrowColor: theme.header, // Beacon color
          backgroundColor: theme.appearance.color, // Bg color
          textColor: theme.appearance.text, // All text, title color
          zIndex: 1000000000
        }
      }}
      locale={{
        back: t('Go Back'),
        last: t('End tour'),
        start: t('Start tour')
      }}
    />
  );
}
